import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  ViewList,
  ViewModule,
  ViewAgenda,
  AccessTime,
  Description,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../landingPage/Sidebar';
import { PageHeader } from '../../components/ui/header';
import { FilterSelect } from '../../components/ui/select';
import { DataTable } from '../../components/ui/table';
import { PrimaryButton } from '../../components/ui/button';
import successIcon from './images/SuccessIcon.png';
import failedIcon from './images/FailedIcon.png';
import runningIcon from './images/RunningIcon.png';
import partialSuccessIcon from './images/partialSuccessIcon.png';
import viewDetailsIcon from './images/viewDestailsIcon.png';
import reexecuteIcon from './images/ReexecuteIcon.png';
import downloadIcon from './images/DownloadIcon.png';
import scheduleIcon from './images/ScheduleIcon.png';
import manualIcon from './images/ManualIcon.png';

const statusChipPalette = {
  Success: { bgLight: '#DCFCE7', text: '#008236', border: '#008236', icon: successIcon },
  Failed: { bgLight: '#FFE2E2', text: '#C10007', border: '#C10007', icon: failedIcon },
  Running: { bgLight: '#DBEAFE', text: '#1447E6', border: '#1447E6', icon: runningIcon },
  'Partial Success': { bgLight: '#FFEDD4', text: '#D87000', border: '#D87000', icon: partialSuccessIcon },
};

const getStatusBarColor = (status) => {
  const normalizedStatus = (status || '').toLowerCase();
  switch (normalizedStatus) {
    case 'success':
      return '#008236';
    case 'failed':
      return '#C10007';
    case 'running':
      return '#1447E6';
    case 'partial success':
      return '#D87000';
    default:
      return '#9CA3AF';
  }
};

// Dummy data matching the images
const dummyPipelineRuns = [
  {
    id: 'RUN-20260119-001',
    timestamp: '2026-01-19 10:32:15',
    pipelineName: 'Salesforce to Postgres',
    type: 'ETL',
    status: 'Success',
    trigger: 'Scheduled',
    duration: '1m 57s',
    records: 11800,
  },
  {
    id: 'RUN-20260119-0011',
    timestamp: '2026-01-12 11:32:15',
    pipelineName: 'HubSpot Sync',
    type: 'ETL',
    status: 'Failed',
    trigger: 'Manual',
    duration: '3m 57s',
    records: 7800,
  },
  {
    id: 'RUN-20260119-002',
    timestamp: '2026-01-19 10:32:15',
    pipelineName: 'Salesforce to Postgres',
    type: 'ETL',
    status: 'Running',
    trigger: 'Scheduled',
    duration: '1m 57s',
    records: 11800,
  },
  {
    id: 'RUN-20260119-003',
    timestamp: '2026-01-19 10:32:15',
    pipelineName: 'Salesforce to Postgres',
    type: 'ETL',
    status: 'Partial Success',
    trigger: 'Scheduled',
    duration: '1m 57s',
    records: 11800,
  },
  {
    id: 'RUN-20260119-004',
    timestamp: '2026-01-19 10:32:15',
    pipelineName: 'Salesforce to Postgres',
    type: 'ETL',
    status: 'Running',
    trigger: 'Scheduled',
    duration: '1m 57s',
    records: 11800,
  },
  {
    id: 'RUN-20260119-005',
    timestamp: '2026-01-19 10:32:15',
    pipelineName: 'Salesforce to Postgres',
    type: 'ETL',
    status: 'Success',
    trigger: 'Scheduled',
    duration: '1m 57s',
    records: 11800,
  },
  {
    id: 'RUN-20260119-006',
    timestamp: '2026-01-19 10:32:15',
    pipelineName: 'Salesforce to Postgres',
    type: 'ETL',
    status: 'Success',
    trigger: 'Scheduled',
    duration: '1m 57s',
    records: 11800,
  },
  {
    id: 'RUN-20260119-0012',
    timestamp: '2026-01-12 11:32:15',
    pipelineName: 'HubSpot Sync',
    type: 'ETL',
    status: 'Failed',
    trigger: 'Manual',
    duration: '3m 57s',
    records: 7800,
  },
  {
    id: 'RUN-20260119-007',
    timestamp: '2026-01-19 10:32:15',
    pipelineName: 'Salesforce to Postgres',
    type: 'ETL',
    status: 'Running',
    trigger: 'Scheduled',
    duration: '1m 57s',
    records: 11800,
  },
];

const PipelinerunsMainpage = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', or 'split'
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedPipeline, setSelectedPipeline] = useState('all');
  const [selectedTimeRange, setSelectedTimeRange] = useState('last24hours');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRunType, setSelectedRunType] = useState('all');
  const [selectedRun, setSelectedRun] = useState(null);

  const uniqueProjects = useMemo(() => {
    return ['Project A', 'Project B', 'Project C'];
  }, []);

  const uniquePipelines = useMemo(() => {
    return ['Salesforce to Postgres', 'HubSpot Sync', 'Data Migration'];
  }, []);

  const statusOptions = useMemo(() => {
    return ['Success', 'Failed', 'Running', 'Partial Success'];
  }, []);

  const runTypeOptions = useMemo(() => {
    return ['Scheduled', 'Manual'];
  }, []);

  const filteredRuns = useMemo(() => {
    let filtered = [...dummyPipelineRuns];

    if (selectedStatus !== 'all') {
      filtered = filtered.filter((run) => run.status === selectedStatus);
    }

    if (selectedRunType !== 'all') {
      filtered = filtered.filter((run) => run.trigger === selectedRunType);
    }

    return filtered;
  }, [selectedStatus, selectedRunType]);

  // Set first run as selected by default when filtered runs change
  useEffect(() => {
    if (filteredRuns.length > 0 && !selectedRun) {
      setSelectedRun(filteredRuns[0]);
    } else if (filteredRuns.length > 0 && selectedRun) {
      // Update selected run if it still exists in filtered list
      const stillExists = filteredRuns.some((run) => run.id === selectedRun.id && run.timestamp === selectedRun.timestamp);
      if (!stillExists) {
        setSelectedRun(filteredRuns[0]);
      }
    }
  }, [filteredRuns, selectedRun]);

  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value);
  };

  const handlePipelineChange = (event) => {
    setSelectedPipeline(event.target.value);
  };

  const handleTimeRangeChange = (event) => {
    setSelectedTimeRange(event.target.value);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleRunTypeChange = (event) => {
    setSelectedRunType(event.target.value);
  };

  const handleView = (runId) => {
    navigate(`/pipeline/${runId}`);
  };

  const handleRerun = (runId) => {
    console.log('Rerun:', runId);
    // Trigger rerun
  };

  const handleDownload = (runId) => {
    console.log('Download:', runId);
    // Download run data
  };

  const renderStatusChip = (status) => {
    const palette = statusChipPalette[status] || statusChipPalette.Failed;
    const isRunning = status === 'Running';

    return (
      <Chip
        icon={
          <Box
            component="img"
            src={palette.icon}
            alt={status}
            sx={{
              width: 14,
              height: 14,
              ...(isRunning && {
                animation: 'spin 2s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }),
            }}
          />
        }
        label={status}
        sx={{
          height: 28,
          fontSize: '0.75rem',
          fontWeight: 500,
          fontFamily: 'Inter, sans-serif',
          bgcolor: palette.bgLight,
          color: palette.text,
          border: `1px solid ${palette.border}`,
          borderRadius: '6px',
          '& .MuiChip-icon': {
            marginLeft: '8px',
            marginRight: '0',
          },
        }}
      />
    );
  };

  const renderGridCard = (run, index) => {
    const statusBarColor = getStatusBarColor(run.status);

    return (
      <motion.div
        key={run.id + index}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05, duration: 0.3 }}
        whileHover={{ y: -4 }}
      >
        <Card
          sx={{
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #E6E6E6',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              bgcolor: statusBarColor,
            }}
          />
          <CardContent sx={{ p: 1.5, pl: 2.5, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#525252',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  E
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#0F172A',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: 1.2,
                      mb: 0.25,
                    }}
                  >
                    {run.id}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: 1.2,
                    }}
                  >
                    {run.timestamp}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="View Details" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleView(run.id)}
                    sx={{
                      p: 0.5,
                      color: '#525252',
                      '&:hover': { bgcolor: '#F3F4F6', color: '#2F5D63' },
                    }}
                  >
                    <Box
                      component="img"
                      src={viewDetailsIcon}
                      alt="View Details"
                      sx={{ width: 18, height: 18 }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rerun" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleRerun(run.id)}
                    sx={{
                      p: 0.5,
                      color: '#525252',
                      '&:hover': { bgcolor: '#F3F4F6', color: '#2F5D63' },
                    }}
                  >
                    <Box
                      component="img"
                      src={reexecuteIcon}
                      alt="Rerun"
                      sx={{ width: 18, height: 18 }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(run.id)}
                    sx={{
                      p: 0.5,
                      color: '#525252',
                      '&:hover': { bgcolor: '#F3F4F6', color: '#2F5D63' },
                    }}
                  >
                    <Box
                      component="img"
                      src={downloadIcon}
                      alt="Download"
                      sx={{ width: 18, height: 18 }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#0F172A',
                fontFamily: 'Inter, sans-serif',
                mb: 0.5,
                lineHeight: 1.3,
              }}
            >
              {run.pipelineName.includes('Salesforce') ? 'Salesforce → Postgres' : 'HubSpot → Database'}
            </Typography>

            <Box
              sx={{
                height: '1px',
                bgcolor: '#E6E6E6',
                my: 1,
              }}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  component="img"
                  src={run.trigger === 'Scheduled' ? scheduleIcon : manualIcon}
                  alt={run.trigger}
                  sx={{ width: 14, height: 14 }}
                />
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: '#525252',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {run.trigger}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime sx={{ fontSize: 14, color: '#6B7280' }} />
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: '#525252',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {run.duration}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Description sx={{ fontSize: 14, color: '#6B7280' }} />
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: '#525252',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {run.records.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            <Box>
              {renderStatusChip(run.status)}
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  const tableColumns = useMemo(() => [
    {
      key: 'runId',
      label: 'Run ID/ Timestamp',
      sortable: true,
      render: (row) => (
        <Box>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: '#0F172A',
              fontFamily: 'Inter, sans-serif',
              mb: 0.25,
            }}
          >
            {row.id}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#6B7280',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {row.timestamp}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'pipelineName',
      label: 'Pipeline name',
      sortable: true,
      cellSx: {
        fontSize: '0.8125rem',
        color: '#525252',
        fontFamily: 'Inter, sans-serif',
      },
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      cellSx: {
        fontSize: '0.8125rem',
        color: '#525252',
        fontFamily: 'Inter, sans-serif',
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => renderStatusChip(row.status),
    },
    {
      key: 'trigger',
      label: 'Trigger',
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="img"
            src={row.trigger === 'Scheduled' ? scheduleIcon : manualIcon}
            alt={row.trigger}
            sx={{ width: 16, height: 16 }}
          />
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: '#525252',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {row.trigger}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'duration',
      label: 'Duration',
      sortable: true,
      cellSx: {
        fontSize: '0.8125rem',
        color: '#525252',
        fontFamily: 'Inter, sans-serif',
      },
    },
    {
      key: 'records',
      label: 'Records',
      sortable: true,
      render: (row) => row.records.toLocaleString(),
      cellSx: {
        fontSize: '0.8125rem',
        color: '#525252',
        fontFamily: 'Inter, sans-serif',
      },
    },
  ], []);

  const renderTableActions = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title="View Details" arrow>
        <IconButton
          size="small"
          onClick={() => handleView(row.id)}
          sx={{
            p: 0.5,
            color: '#525252',
            '&:hover': { bgcolor: '#F3F4F6', color: '#2F5D63' },
          }}
        >
          <Box
            component="img"
            src={viewDetailsIcon}
            alt="View Details"
            sx={{ width: 18, height: 18 }}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="Rerun" arrow>
        <IconButton
          size="small"
          onClick={() => handleRerun(row.id)}
          sx={{
            p: 0.5,
            color: '#525252',
            '&:hover': { bgcolor: '#F3F4F6', color: '#2F5D63' },
          }}
        >
          <Box
            component="img"
            src={reexecuteIcon}
            alt="Rerun"
            sx={{ width: 18, height: 18 }}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download" arrow>
        <IconButton
          size="small"
          onClick={() => handleDownload(row.id)}
          sx={{
            p: 0.5,
            color: '#525252',
            '&:hover': { bgcolor: '#F3F4F6', color: '#2F5D63' },
          }}
        >
          <Box
            component="img"
            src={downloadIcon}
            alt="Download"
            sx={{ width: 18, height: 18 }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const isRunSelected = (run) => {
    return selectedRun && run.id === selectedRun.id && run.timestamp === selectedRun.timestamp;
  };

  const renderSplitView = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          height: { xs: 'auto', md: 'calc(100vh - 300px)' },
          minHeight: { xs: 'auto', md: 600 },
        }}
      >
        {/* Left Panel - Pipeline Runs List */}
        <Box
          sx={{
            width: { xs: '100%', md: '65%' },
            height: { xs: '400px', md: '100%' },
            bgcolor: 'white',
            borderRadius: '8px',
            border: '1px solid #E6E6E6',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: 1.75,
              borderBottom: '1px solid #E6E6E6',
              bgcolor: '#FAFBFD',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#0F172A',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Pipeline Runs
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 1.25,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#F3F4F6',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#D1D5DB',
                borderRadius: '3px',
                '&:hover': {
                  background: '#9CA3AF',
                },
              },
            }}
          >
            {filteredRuns.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  color: '#6B7280',
                }}
              >
                <Typography sx={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                  No pipeline runs found.
                </Typography>
              </Box>
            ) : (
              filteredRuns.map((run, index) => {
                const isSelected = isRunSelected(run);
                return (
                  <motion.div
                    key={run.id + index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03, duration: 0.25, ease: 'easeOut' }}
                  >
                    <Box
                      onClick={() => setSelectedRun(run)}
                      sx={{
                        p: 1.5,
                        mb: 0.75,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        bgcolor: isSelected ? '#ECFDF5' : 'transparent',
                        border: isSelected ? `1px solid #3B9562` : '1px solid transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&:hover': {
                          bgcolor: isSelected ? '#ECFDF5' : '#FAFBFD',
                          border: isSelected ? `1px solid #3B9562` : '1px solid #E6E6E6',
                          transform: 'translateX(2px)',
                        },
                        '&::before': isSelected ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '3px',
                          bgcolor: '#3B9562',
                          borderRadius: '8px 0 0 8px',
                        } : {},
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              color: '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                              mb: 0.5,
                              lineHeight: 1.3,
                            }}
                          >
                            {run.pipelineName.includes('Salesforce') ? 'Salesforce → Postgres' : 'HubSpot → Database'}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6B7280',
                              fontFamily: 'Inter, sans-serif',
                              mb: 0.25,
                            }}
                          >
                            {run.id}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6B7280',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            {run.timestamp}
                          </Typography>
                        </Box>
                        <Box sx={{ ml: 1.5 }}>
                          {renderStatusChip(run.status)}
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })
            )}
          </Box>
        </Box>

        {/* Right Panel - Run Details */}
        <Box
          sx={{
            width: { xs: '100%', md: '35%' },
            height: { xs: 'auto', md: '100%' },
            bgcolor: 'white',
            borderRadius: '8px',
            border: '1px solid #E6E6E6',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AnimatePresence mode="wait">
            {selectedRun ? (
              <motion.div
                key={selectedRun.id + selectedRun.timestamp}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: '1px solid #E6E6E6',
                  bgcolor: '#FAFBFD',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#0F172A',
                    fontFamily: 'Inter, sans-serif',
                    mb: 0.25,
                  }}
                >
                  Run Details
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: '#6B7280',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Investigation & Debug Mode
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 1.5,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#F3F4F6',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#D1D5DB',
                    borderRadius: '3px',
                    '&:hover': {
                      background: '#9CA3AF',
                    },
                  },
                }}
              >
                {/* Metadata Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      mb: 1,
                    }}
                  >
                    Metadata
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1,px: 1 }}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.25,
                        }}
                      >
                        Run ID
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: '#0F172A',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {selectedRun.id}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.25,
                        }}
                      >
                        Pipeline Name
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: '#0F172A',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {selectedRun.pipelineName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.25,
                        }}
                      >
                        Timestamp
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14, color: '#6B7280' }} />
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: '#0F172A',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {selectedRun.timestamp}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.25,
                        }}
                      >
                        Trigger
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          component="img"
                          src={selectedRun.trigger === 'Scheduled' ? scheduleIcon : manualIcon}
                          alt={selectedRun.trigger}
                          sx={{ width: 14, height: 14 }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: '#0F172A',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {selectedRun.trigger}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.25,
                        }}
                      >
                        Duration
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14, color: '#6B7280' }} />
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: '#0F172A',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {selectedRun.duration}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: '#6B7280',
                          fontFamily: 'Inter, sans-serif',
                          mb: 0.25,
                        }}
                      >
                        Type
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Description sx={{ fontSize: 14, color: '#6B7280' }} />
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: '#0F172A',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          {selectedRun.type}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Status Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      mb: 1,
                    }}
                  >
                    Status
                  </Typography>
                  {renderStatusChip(selectedRun.status)}
                </Box>

                {/* Key Metrics Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      fontFamily: 'Inter, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      mb: 1,
                    }}
                  >
                    Key Metrics
                  </Typography>
                  <Box
                    sx={{
                      p: 1.25,
                      bgcolor: ' #F1F5FF',
                      borderRadius: '4px',
                      border: '1px solid #E6E6E6',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        color: '#101828',
                        fontFamily: 'Inter, sans-serif',
                        mb: 0.25,
                      }}
                    >
                      {selectedRun.records >= 1000000
                        ? `${(selectedRun.records / 1000000).toFixed(1)}M`
                        : selectedRun.records >= 1000
                          ? `${(selectedRun.records / 1000).toFixed(1)}K`
                          : selectedRun.records.toLocaleString()}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: '#4A5565',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      Records Processed
                    </Typography>
                  </Box>
                </Box>

                {/* More Details Button */}
                <Box>
                  <PrimaryButton
                    variant="contained"
                    fullWidth
                    onClick={() => handleView(selectedRun.id)}
                    startIcon={
                      <Box
                        component="img"
                        src={viewDetailsIcon}
                        alt="View Details"
                        sx={{ width: 16, height: 16 }}
                      />
                    }
                    sx={{
                      fontSize: '0.8125rem',
                      py: 1,
                      textTransform: 'none',
                    }}
                  >
                    More Details
                  </PrimaryButton>
                </Box>
              </Box>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Typography sx={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                  Select a pipeline run to view details
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        bgcolor: '#FAFBFD',
        background: '#FAFBFD',
        minHeight: '100vh',
      }}
    >
      <Sidebar />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          title="Pipeline Runs"
          showConnectorIcon={false}
          createButtonText={null}
        />

        <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5, md: 2 },
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5, md: 2 },
                flex: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <FilterSelect
                type="select"
                value={selectedProject}
                onChange={handleProjectChange}
                options={uniqueProjects}
                allOption={true}
                allOptionLabel="All Projects"
                allOptionValue="all"
                sx={{ minWidth: { xs: '100%', sm: 140, md: 160 } }}
              />

              <FilterSelect
                type="select"
                value={selectedPipeline}
                onChange={handlePipelineChange}
                options={uniquePipelines}
                allOption={true}
                allOptionLabel="All Pipelines"
                allOptionValue="all"
                sx={{ minWidth: { xs: '100%', sm: 140, md: 160 } }}
              />

              <FilterSelect
                type="select"
                value={selectedTimeRange}
                onChange={handleTimeRangeChange}
                options={[
                  { value: 'last24hours', label: 'Last 24 hours' },
                  { value: 'last7days', label: 'Last 7 Days' },
                  { value: 'last30days', label: 'Last 30 Days' },
                ]}
                allOption={true}
                allOptionLabel="All Time"
                allOptionValue="all"
                sx={{ minWidth: { xs: '100%', sm: 140, md: 160 } }}
              />

              <FilterSelect
                type="select"
                value={selectedStatus}
                onChange={handleStatusChange}
                options={statusOptions}
                allOption={true}
                allOptionLabel="All Status"
                allOptionValue="all"
                sx={{ minWidth: { xs: '100%', sm: 120, md: 140 } }}
              />

              <FilterSelect
                type="select"
                value={selectedRunType}
                onChange={handleRunTypeChange}
                options={runTypeOptions}
                allOption={true}
                allOptionLabel="All Run Types"
                allOptionValue="all"
                sx={{ minWidth: { xs: '100%', sm: 120, md: 140 } }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'white',
                border: '1px solid #E6E6E6',
                borderRadius: '4px',
                p: 0.5,
                boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
              }}
            >
              <Tooltip title="List View" arrow>
                <IconButton
                  size="small"
                  onClick={() => setViewMode('list')}
                  sx={{
                    bgcolor: viewMode === 'list' ? '#ecfdf5' : 'transparent',
                    '&:hover': { bgcolor: viewMode === 'list' ? '#ecfdf5' : '#f5f5f5' },
                    p: 0.75,
                  }}
                >
                  <ViewList
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      color: viewMode === 'list' ? '#3b9562' : '#525252',
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Grid View" arrow>
                <IconButton
                  size="small"
                  onClick={() => setViewMode('grid')}
                  sx={{
                    bgcolor: viewMode === 'grid' ? '#ecfdf5' : 'transparent',
                    '&:hover': { bgcolor: viewMode === 'grid' ? '#ecfdf5' : '#f5f5f5' },
                    p: 0.75,
                  }}
                >
                  <ViewModule
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      color: viewMode === 'grid' ? '#3b9562' : '#525252',
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Split View" arrow>
                <IconButton
                  size="small"
                  onClick={() => setViewMode('split')}
                  sx={{
                    bgcolor: viewMode === 'split' ? '#ecfdf5' : 'transparent',
                    '&:hover': { bgcolor: viewMode === 'split' ? '#ecfdf5' : '#f5f5f5' },
                    p: 0.75,
                  }}
                >
                  <ViewAgenda
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                      color: viewMode === 'split' ? '#3b9562' : '#525252',
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <AnimatePresence mode="wait">
            {viewMode === 'grid' ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Grid container spacing={2}>
                  {filteredRuns.map((run, index) => (
                    <Grid item xs={12} sm={6} md={4} key={run.id + index}>
                      {renderGridCard(run, index)}
                    </Grid>
                  ))}
                </Grid>
                {filteredRuns.length === 0 && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 8,
                      color: '#6B7280',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                      No pipeline runs found.
                    </Typography>
                  </Box>
                )}
              </motion.div>
            ) : viewMode === 'list' ? (
              <motion.div
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <DataTable
                  columns={tableColumns}
                  data={filteredRuns}
                  loading={false}
                  showSerialNumber={true}
                  showActions={true}
                  renderActions={renderTableActions}
                  emptyMessage="No pipeline runs found."
                  emptySubMessage=""
                />
                <Box
                  sx={{
                    mt: 2,
                    px: 2,
                    py: 1,
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Showing {filteredRuns.length} of {filteredRuns.length} runs
                </Box>
              </motion.div>
            ) : (
              <motion.div
                key="split"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {renderSplitView()}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  );
};

export default PipelinerunsMainpage;
